import { useCallback, useEffect, useRef } from 'react';

import { useRouter } from 'next/router';

import { DEFAULT_SORT_BY_VALUE } from '../components/growth-stage-ventures/sort-by-menu/constants';
import { ISortByValue } from '../components/growth-stage-ventures/sort-by-menu/types';
import {
  fromSortSlug,
  SORT_QUERY_PARAM,
  toSortSlug,
} from '../utils/sort-query-param';

export interface IUseSortQueryParamResult {
  /** Current sort. `DEFAULT_SORT_BY_VALUE` until the router is ready. */
  sort: ISortByValue;
  /**
   * False on the first client render. Callers should hold a loading state rather
   * than query with a sort they don't know yet.
   */
  isRouterReady: boolean;
  /** Writes the slug, strips it for the default, keeps any `#hash`. */
  setSort: (value: ISortByValue) => void;
}

/**
 * Keeps the growth-stage sort in `?sort=`, so a sorted view can be linked and
 * survives a reload.
 *
 * The page has no `getServerSideProps`, so Next serves it statically optimized
 * and `router.query` is empty until `isReady` — reading a deep link's sort before
 * then would fetch the default first and re-sort visibly. `isRouterReady` is
 * exposed so the caller can wait instead.
 */
export const useSortQueryParam = (): IUseSortQueryParamResult => {
  const router = useRouter();
  // Mocked routers omit `isReady` → treat as ready (same idiom as
  // `us-onboarding-flow`).
  const isRouterReady = router.isReady ?? true;

  // The router object identity is not stable, so it must never reach a dep array
  // — a `replace` in an effect keyed on it would re-fire on every render. Read
  // through a ref instead and keep the deps below to primitives. Don't "fix"
  // this by adding `router` back.
  const routerRef = useRef(router);
  routerRef.current = router;

  // Read only once ready: before that `query` is `{}`, which is
  // indistinguishable from a URL that carries no sort.
  const rawSort = isRouterReady ? router.query[SORT_QUERY_PARAM] : undefined;
  const parsedSort = fromSortSlug(rawSort);
  const sort = parsedSort ?? DEFAULT_SORT_BY_VALUE;

  const replaceSortParam = useCallback((slug: string | null) => {
    const { pathname, query, asPath } = routerRef.current;
    const { [SORT_QUERY_PARAM]: _omitted, ...restQuery } = query;

    // The fragment is not part of `router.query`, and a `UrlObject` replace
    // rebuilds the whole URL — so without carrying it over,
    // `?sort=…#growth-stage-ventures` would lose its anchor on every change.
    // `asPath` is the only place it survives.
    const hashIndex = asPath.indexOf('#');
    const hash = hashIndex === -1 ? undefined : asPath.slice(hashIndex);

    void routerRef.current.replace(
      {
        // `pathname`, not `asPath`: the query object stays authoritative.
        pathname,
        query: slug ? { ...restQuery, [SORT_QUERY_PARAM]: slug } : restQuery,
        ...(hash ? { hash } : {}),
      },
      undefined,
      // `scroll: false`: the grid reloads in place, so the viewport must stay
      // put — jumping to the top would also undo a deep link's anchor.
      { shallow: true, scroll: false }
    );
  }, []);

  // A param that doesn't name a non-default sort has no business staying in the
  // URL: `?sort=banana` is junk, and `?sort=trending` contradicts "absent means
  // trending". Idempotent — once stripped, `rawSort` is undefined and this
  // short-circuits.
  useEffect(() => {
    if (!isRouterReady || rawSort === undefined || parsedSort) {
      return;
    }

    replaceSortParam(null);
    // `rawSort` can be a `string[]`, whose identity changes every render;
    // stringified so the dep stays a primitive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRouterReady, String(rawSort), parsedSort, replaceSortParam]);

  const setSort = useCallback(
    (value: ISortByValue) => {
      replaceSortParam(toSortSlug(value));
    },
    [replaceSortParam]
  );

  return { sort, isRouterReady, setSort };
};
